/**
 * Owner: Masa Bayu
 * File: component-primitive.ts
 * Date:Time: 2026-09-13 21:46
 * Description: 22F framework-neutral component primitive semantics.
 */
import { createQXDesignTokenReference, parseQXDesignTokenPath, } from './design-token';
export const QX_COMPONENT_PRIMITIVE_FAMILIES = [
    'text',
    'surface',
    'container',
    'stack',
    'divider',
    'icon',
    'media',
    'action',
];
export const QX_COMPONENT_PRIMITIVE_STATES = [
    'default',
    'hover',
    'active',
    'focus',
    'disabled',
    'selected',
    'busy',
];
/** Minimal semantic roles per family — extensible later in 22G, not eBook-specific. */
export const QX_COMPONENT_PRIMITIVE_ROLES = {
    text: ['display', 'title', 'body', 'caption', 'label'],
    surface: ['canvas', 'panel', 'raised'],
    container: ['section', 'region', 'group'],
    stack: ['vertical', 'horizontal'],
    divider: ['horizontal', 'vertical'],
    icon: ['decorative', 'informative'],
    media: ['image', 'figure'],
    action: ['button', 'link', 'toggle'],
};
const FORBIDDEN = [
    'react',
    'jsx',
    'dom',
    'html',
    'css',
    'className',
    'style',
    'render',
    'renderer',
    'children',
    'props',
    'element',
    'node',
    'qxw',
    'qxp',
    'widget',
];
function assertNoForbidden(record, label) {
    for (const key of FORBIDDEN) {
        if (key in record) {
            throw new Error(`${label} must not include forbidden field "${key}"`);
        }
    }
}
function assertId(value, label) {
    if (typeof value !== 'string' || value.trim().length === 0 || /\s/.test(value)) {
        throw new Error(`${label} must be a non-empty id without whitespace`);
    }
}
export function isQXComponentPrimitiveFamily(value) {
    return (typeof value === 'string' &&
        QX_COMPONENT_PRIMITIVE_FAMILIES.includes(value));
}
export function isQXComponentPrimitiveState(value) {
    return (typeof value === 'string' &&
        QX_COMPONENT_PRIMITIVE_STATES.includes(value));
}
export function isQXComponentPrimitiveRole(family, role) {
    if (!isQXComponentPrimitiveFamily(family) || typeof role !== 'string') {
        return false;
    }
    const allowed = QX_COMPONENT_PRIMITIVE_ROLES[family];
    return allowed.includes(role);
}
export function createQXComponentPrimitiveAccessibility(options) {
    assertNoForbidden({ ...options }, 'Component primitive accessibility');
    assertId(options.a11yRole, 'Accessibility a11yRole');
    assertId(options.labelKey, 'Accessibility labelKey');
    if (options.descriptionKey !== undefined) {
        assertId(options.descriptionKey, 'Accessibility descriptionKey');
    }
    if (typeof options.focusable !== 'boolean') {
        throw new Error('Accessibility focusable must be a boolean');
    }
    return Object.freeze({
        a11yRole: options.a11yRole.trim(),
        labelKey: options.labelKey.trim(),
        ...(options.descriptionKey !== undefined
            ? { descriptionKey: options.descriptionKey.trim() }
            : {}),
        focusable: options.focusable,
    });
}
function normalizeToken(token) {
    if (typeof token === 'string') {
        return parseQXDesignTokenPath(token);
    }
    return createQXDesignTokenReference(token.group, token.name);
}
export function createQXComponentPrimitive(options) {
    assertNoForbidden({ ...options }, 'Component primitive');
    assertNoForbidden({ ...options.identity }, 'Component primitive identity');
    if (!isQXComponentPrimitiveFamily(options.identity.family)) {
        throw new Error(`Invalid component primitive family: ${String(options.identity.family)}`);
    }
    assertId(options.identity.id, 'Component primitive id');
    if (typeof options.identity.name !== 'string' || options.identity.name.trim().length === 0) {
        throw new Error('Component primitive name must be a non-empty string');
    }
    if (!isQXComponentPrimitiveRole(options.identity.family, options.semanticRole)) {
        throw new Error(`Invalid semanticRole "${options.semanticRole}" for family "${options.identity.family}"`);
    }
    const tokens = options.tokens.map((token) => {
        if (typeof token !== 'string') {
            assertNoForbidden({ ...token }, 'Component primitive token');
        }
        return normalizeToken(token);
    });
    const tokenKeys = new Set();
    for (const token of tokens) {
        const key = `${token.group}.${token.name}`;
        if (tokenKeys.has(key)) {
            throw new Error(`Duplicate token reference on primitive: ${key}`);
        }
        tokenKeys.add(key);
    }
    const states = [...options.states];
    if (states.length === 0) {
        throw new Error('Component primitive must declare at least one state');
    }
    if (!states.includes('default')) {
        throw new Error('Component primitive states must include "default"');
    }
    const seenStates = new Set();
    for (const state of states) {
        if (!isQXComponentPrimitiveState(state)) {
            throw new Error(`Invalid component primitive state: ${String(state)}`);
        }
        if (seenStates.has(state)) {
            throw new Error(`Duplicate component primitive state: ${state}`);
        }
        seenStates.add(state);
    }
    const accessibility = createQXComponentPrimitiveAccessibility(options.accessibility);
    return Object.freeze({
        identity: Object.freeze({
            id: options.identity.id.trim(),
            name: options.identity.name.trim(),
            family: options.identity.family,
        }),
        semanticRole: options.semanticRole,
        tokens: Object.freeze(tokens),
        states: Object.freeze(states),
        accessibility,
    });
}
export function isQXComponentPrimitive(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    if (typeof record.identity !== 'object' || record.identity === null) {
        return false;
    }
    return (typeof record.semanticRole === 'string' &&
        Array.isArray(record.tokens) &&
        Array.isArray(record.states) &&
        typeof record.accessibility === 'object' &&
        record.accessibility !== null);
}
//# sourceMappingURL=component-primitive.js.map